// MACHINERY V2 (Stroje) — V4 shop-floor wall: the way OUT (M2.14 fix pass).
//
// ============================================================
//  THE WALL WAS A ONE-WAY DOOR. THIS IS THE HANDLE.
// ============================================================
// Kiosk layout removes the toolbar, therefore the view toggle; and `fleetView`
// is localStorage-persisted (M2.13 ruling 3). So before this file, a laptop
// user who clicked the wall glyph out of curiosity was stuck on the wall across
// reloads, with no in-page control and no recovery short of hand-clearing
// `machinery-v2-fleet-view`. Owner ruling: give it an exit, but put no chrome
// on a hall TV.
//
// TWO MECHANISMS, ONE OF WHICH IS INVISIBLE:
//   · Esc leaves the wall. Keyboard only, zero pixels, works on the first press
//     with no discovery step. This is THE exit.
//   · A transient chip TEACHES Esc — the video-player pattern (controls appear
//     when you move the mouse, fade when you stop). It is kiosk chrome, not an
//     interaction on the data surface: NO click handler and `pointerEvents:
//     none`, so it cannot be the thing that navigates and cannot swallow a
//     click. The ticket's "NO interactions" holds for everything the wall
//     renders about a machine.
//
// >>> A TV HAS NO POINTER, SO THE CHIP MUST NEVER RENDER ON ONE. <<< `seen`
// starts FALSE and is flipped only by a real `pointermove` event — never on
// mount, never on a timer, never from a render-time guess about the device — so
// on an unattended screen this component renders literally nothing, forever.
// Touch pointers are excluded too: a tap on a touch overlay is not "a user with
// a mouse hunting for the way out", and M2.16 owns the phone/tablet surface.
// `seen` is separate from `visible` only so the chip can FADE rather than
// vanish: once a pointer exists the node stays mounted at opacity 0.
//
// IT OWNS ITS OWN STATE, IN A LEAF, for the same reason WallLiveClock does: a
// hint that re-rendered FleetWallView would rebuild the whole tile grid every
// time a mouse crossed the screen.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useEffect, useState } from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";

/** How long the chip stays up after the pointer stops moving. Long enough to
 *  read four words, short enough that a passing mouse leaves no residue. */
export const WALL_HINT_IDLE_MS = 3000;

export interface WallExitHintProps {
  /** Leaves the wall. Bound to Esc — never to a click on the chip. */
  onExit: () => void;
}

const WallExitHint: React.FC<WallExitHintProps> = ({ onExit }) => {
  const { translate } = useTranslation("myMachinery");
  const [seen, setSeen] = useState(false);
  const [visible, setVisible] = useState(false);

  // Both listeners are on `window` and both are removed on unmount, so leaving
  // the wall takes the Esc binding with it — the key does nothing anywhere
  // else in the fleet.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onExit();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onExit]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const onPointerMove = (e: PointerEvent) => {
      // See the header: a touch pointer is not the case this exists for.
      if (e.pointerType === "touch") return;
      setSeen(true);
      setVisible(true);
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setVisible(false), WALL_HINT_IDLE_MS);
    };
    window.addEventListener("pointermove", onPointerMove);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener("pointermove", onPointerMove);
    };
  }, []);

  if (!seen) return null;

  return (
    <div
      style={{
        position: "absolute",
        right: 20,
        bottom: 20,
        padding: "7px 13px",
        borderRadius: 999,
        background: "var(--card)",
        border: "1px solid var(--border)",
        boxShadow: "0 2px 10px rgba(0,0,0,0.10)",
        fontFamily: "var(--font-mono)",
        fontSize: 12,
        letterSpacing: "0.04em",
        color: "var(--ink-2)",
        whiteSpace: "nowrap",
        opacity: visible ? 1 : 0,
        transition: "opacity 400ms ease",
        pointerEvents: "none",
      }}
    >
      {translate("myMachinery:v2.fleet.wall.exitHint")}
    </div>
  );
};

export default WallExitHint;
